import { Injectable } from '@angular/core';
import { httpsCallable } from 'firebase/functions';
import { addEffect, addEffectOrEffectLayer, addNewLayer, changeLayerOrder, cloneLayer, createPersonalPreset, defaultEditorState, loadPreset, resetEditorState, saveGroupRenderToFirebase, savePng, saveRenderToFirebase, saveRenderToFirebaseSuccess, saveZip, selectEffect, setRender, setSelectedLayer, updateLayer } from './editor.actions';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { EMPTY, distinctUntilChanged, filter, map, switchMap, tap, throttleTime, withLatestFrom } from 'rxjs';
import { Action, Store, select } from '@ngrx/store';
import { BaseState } from '../../reducers';
import { selectEditorState, selectLayerOrder, selectLayers, selectRenderId, selectRenderName, selectSelectedEffect, selectSelectedLayer } from './editor.selector';
import { generateUUID } from '../utils/store.utils';
import { Layer, LayerType } from '../models/layer.model';
import { MessageService } from 'primeng/api';
import { ImageDrawService } from '../../services/image-draw.service';
import { buildNewLayer, cleanGroupedMapping } from './editor.utils';
import { FirebaseFirestoreService } from '../../services/firebase-firestore.service';
import { FirebaseRenderData } from '../../services/firebase.model';
import objectHash from 'object-hash';
import { EditorState } from './editor.reducer';
import { constructBlankRender } from '../../services/imageDraw.utils';
import { EffectsType } from '../models/ui-effects.model';
import { RawImageDataLoaderService } from '../../services/image-loader.service';
import { FirebaseFunctionService } from '../../services/firebase-function.service';
import { selectGroupId } from '../../users/store/user.selector';

@Injectable()
export class EditorEffects {

  blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          // Remove the data URL prefix to get only the base64 part
          resolve(reader.result.split(',')[1]);
        } else {
          reject(new Error('Failed to convert blob to base64 string.'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  cloneLayer$ = createEffect(() => this.actions$.pipe(
    ofType(cloneLayer),
    withLatestFrom(
      this.store.select(selectLayers),
      this.store.select(selectLayerOrder)
    ),
    switchMap(([{layerId}, layers, layerOrder]) => {
      const index = layerOrder.findIndex(x => x === layerId)
      if (index === -1) {
        return EMPTY
      }
      const newID = generateUUID()
      const layerClone = JSON.parse(JSON.stringify(layers[layerId])) as Layer
      layerClone.id = newID
      return [addNewLayer({layer: layerClone}), changeLayerOrder({id: newID, index: index + 1})]
    })
  ));

  addEffectOrEffectLayer$ = createEffect(() => this.actions$.pipe(
    ofType(addEffectOrEffectLayer),
    withLatestFrom(
      this.store.select(selectSelectedLayer),
      this.store.select(selectLayers),
    ),
    switchMap(([{layer}, selectedLayer, layers]) => {
      if (layer.isLayer) {
        return [addNewLayer({layer: buildNewLayer(layer.type as LayerType, this.imageDataService.getStringEncodings())})]
      }
      if (!selectedLayer) {
        this.messageService.add({severity: 'warn', summary: 'Select a layer', detail: `Must select a layer to add a layer effect`});
        return EMPTY
      }
      if (layers[selectedLayer].layerType === LayerType.Base && layer.type === EffectsType.Outline){
        this.messageService.add({severity: 'warn', summary: `No effects on Base layer`, detail: `You are not allowed to outline the Base layer`});
        return EMPTY
      }
      if (layers[selectedLayer].effects.find(x => x.effectType === layer.type)) {
        this.messageService.add({severity: 'warn', summary: `Duplicate Effect`, detail: `You may only have one ${layer.type.toLowerCase()} effect per layer`});
        return EMPTY
      }
      return [addEffect({layerId: selectedLayer, effect: layer})]
    })
  ));

  updateCanvasUponLayerChanges$ = createEffect(() => this.store.pipe(() =>
    this.store.pipe(
      select(selectEditorState),
      distinctUntilChanged((prev, curr) =>{
        const current = {
          ...curr,
          selectedLayer: undefined,
          selectEffect: undefined,
          name: undefined,
        }
        const previous = {
          ...prev,
          selectedLayer: undefined,
          selectEffect: undefined,
         name: undefined,
        }
        return objectHash(previous) === objectHash(current)
      }),
      tap(state => {
        this.imageDrawService.pushImageData.emit(JSON.parse(JSON.stringify(state)))
      })
    )
  ),
  {dispatch: false}
  )

  deselectEffectOnSwitchingLayers$ = createEffect(() => this.actions$.pipe(
    ofType(setSelectedLayer),
    withLatestFrom(
      this.store.select(selectSelectedLayer),
      this.store.select(selectLayers),
      this.store.select(selectSelectedEffect)
    ),
    switchMap(([{layerId}, selectedLayer, layerList, selectedEffect]) => {
      if (!selectedLayer) {
        return [selectEffect({})]
      }
      if (!layerList[selectedLayer].effects.find(x => x.id === selectedEffect)) {
        return [selectEffect({})]
      }
      return EMPTY
    })
  ));


  resetEditorState$ = createEffect(() => this.actions$.pipe(
    ofType(resetEditorState),
    switchMap(() => {
      const baseLayer = buildNewLayer(LayerType.Base, this.imageDataService.getStringEncodings())
       return [loadPreset({
        presetData: {
          layers: {
            [baseLayer.id]: baseLayer
          },
          layerOrder: [baseLayer.id]
        }
      })]
    })
  ))

  ensureEffectLivesOnSelectedLayer$ = createEffect(() => this.actions$.pipe(
    ofType(selectEffect),
    filter(x => x.effectId !== undefined),
    withLatestFrom(
      this.store.select(selectSelectedLayer),
      this.store.select(selectLayers)
    ),
    switchMap(([{effectId}, selectedLayer, layers]) => {
      if (!selectedLayer) {
        const foundLayer = Object.values(layers).find(layer => layer.effects.find(x => x.id === effectId))
        if (foundLayer) {
          return [setSelectedLayer({layerId: foundLayer.id})]
        }
        else {
          return [selectEffect({})]
        }
      }
      if (!layers[selectedLayer].effects.find(x => x.id === effectId)) {
        const foundLayer = Object.values(layers).find(layer => layer.effects.find(x => x.id === effectId))
        if (foundLayer) {
          return [setSelectedLayer({layerId: foundLayer.id})]
        }
      }
      return EMPTY
    })
  ));

  saveImageAsZip = createEffect(() => this.actions$.pipe(
    ofType(saveZip),
    withLatestFrom(
      this.store.select(selectEditorState),
    ),
    tap(async ([action, editorState]) => {
      const fileName = action.filename.split('.')[0]
      const dataLink = await this.imageDrawService.exportRenderZipped(JSON.parse(JSON.stringify(editorState)))
      if (!dataLink) {
        this.messageService.add({severity: 'error', summary: 'Error', detail: `Something went wrong, cannot save file as zip`});
        return
      }

      const link = document.createElement('a');
      link.download = `${fileName}_${editorState.renderName}.zip`;
      link.href = URL.createObjectURL(dataLink)
      link.click();

      action.saveSuccess()
      this.messageService.add({severity: 'success', summary: 'Success', detail: `Layers saved as ${fileName}.zip. Check your downloads folder!`});
    })
  ), {dispatch: false})

  saveImageAsPng = createEffect(() => this.actions$.pipe(
    ofType(savePng),
    withLatestFrom(
      this.store.select(selectEditorState),
    ),
    tap(async ([action, editorState]) => {
      const fileName = action.filename.split('.')[0]
      const dataLink = await this.imageDrawService.exportRenderFullSize(JSON.parse(JSON.stringify(editorState)))

      const link = document.createElement('a');
      link.download = `${fileName}.png`;
      link.href = dataLink
      link.click();

      action.saveSuccess()
      this.messageService.add({severity: 'success', summary: 'Success', detail: `Image saved as ${fileName}.png. Check your downloads folder!`});
    })
  ), {dispatch: false})

  saveRenderToFirebaes = createEffect(() => this.actions$.pipe(
    ofType(saveRenderToFirebase),
    throttleTime(1000),
    withLatestFrom(
      this.store.select(selectLayers),
      this.store.select(selectLayerOrder),
      this.store.select(selectRenderName),
    ),
    map(([_, layers, layerOrder, renderName]) => {
      const output: Partial<FirebaseRenderData> = {
        renderName,
        editorState:  {
          layerOrder,
          layers
        }
      }
      return output
    }),
    distinctUntilChanged((prev, curr) =>{
      return objectHash(prev) === objectHash(curr)
    }
    ),
    withLatestFrom(
      this.store.select(selectRenderId)
    ),
    map(async ([renderData, renderId]) => {
      const mutable = JSON.parse(JSON.stringify(renderData.editorState)) as EditorState
      const image = await this.imageDrawService.createThumbnailImage(mutable)
      const blob = await (await fetch(image)).blob();
      const url = await this.firestoreService.uploadThumbnailImageAndGetAddress(blob, renderId as string)
      return {...renderData, thumbnailImage: url, id: renderId as string}
    }),
    map(async (renderData) => {
      const data = await renderData
      return this.firestoreService.updateRenderData(data)
    }),
    switchMap(() => [saveRenderToFirebaseSuccess()])
  ))

  saveGroupRenderToFirebase = createEffect(() => this.actions$.pipe(
    ofType(saveGroupRenderToFirebase),
    throttleTime(1000),
    withLatestFrom(
      this.store.select(selectLayers),
      this.store.select(selectLayerOrder),
      this.store.select(selectRenderName),
    ),
    map(([_, layers, layerOrder, renderName]) => {
      const output: Partial<FirebaseRenderData> = {
        renderName,
        editorState:  {
          layerOrder,
          layers
        }
      }
      return output
    }),
    distinctUntilChanged((prev, curr) =>{
      return objectHash(prev) === objectHash(curr)
    }
    ),
    withLatestFrom(
      this.store.select(selectRenderId),
      this.store.select(selectGroupId),
    ),
    map(async ([renderData, renderId, groupId]) => {
      const mutable = JSON.parse(JSON.stringify(renderData.editorState)) as EditorState
      const image = await this.imageDrawService.createThumbnailImage(mutable, true)
      const thumbnail = await (await fetch(image)).blob();
      await httpsCallable(this.functions.functions, 'uploadGroupThumbnail')({
        groupId,
        renderId: renderId as string,
        thumbnail: await this.blobToBase64(thumbnail)
      })
      const data = {
        ...renderData,
        id: renderId as string
      }
      return data
    }),
    map(async (renderData) => {
      const data = await renderData
      return this.firestoreService.updateGroupRenderData(data)
    }),
    switchMap(() => [saveRenderToFirebaseSuccess()])
  ))

  createPersonalPreset$ = createEffect(() => this.actions$.pipe(
    ofType(createPersonalPreset),
    withLatestFrom(
      this.store.select(selectEditorState)
    ),
    tap(([action, editorState]) => {
      this.firestoreService.createPersonalPreset({
        name: action.name,
        preset: {
          layers: editorState.layers,
          layerOrder: editorState.layerOrder
        }
      })
    }),
  ), {dispatch: false})

  setEditorToDefault$ = createEffect(() => this.actions$.pipe(
    ofType(defaultEditorState),
    withLatestFrom(
      this.store.select(selectEditorState),
    ),
    tap(([_,editorState]) => {
      const layerData = constructBlankRender()
      this.store.dispatch(setRender({
        layers: layerData.layers,
        layerOrder: layerData.layerOrder,
        renderId: editorState.renderId as string,
        renderName: editorState.renderName
      }))
    })
  ), {dispatch: false})

  setRender$ = createEffect(() => this.actions$.pipe(
    ofType(loadPreset),
    withLatestFrom(
      this.store.select(selectLayers)
    ),
    switchMap(([_,layers]) => {
      const outputActions: Action[] = []

      Object.values(layers).forEach(layer => {
        const modifiedLayer = JSON.parse(JSON.stringify(layer)) as Layer
        const layerModified = cleanGroupedMapping(
          modifiedLayer,
          this.imageDataService.getStringEncodings()
        )
        if (layerModified) {
          outputActions.push(updateLayer({layer: modifiedLayer, layerId: modifiedLayer.id}))
        }
      })

      if (outputActions.length > 0) {
        this.messageService.add({
          severity: 'warn',
          life: 10000,
          summary: 'Layer Assignment Needed',
          detail: `Some building, road, or zone layers could not be matched to this template.
To display the full render, please manually assign the correct layers for buildings, roads, and zones.
Check that all layer names correspond correctly between the template and the current object.`,
        });
      }
      return outputActions
    })
  ))

  constructor(
    private messageService: MessageService,
    private actions$: Actions,
    private store: Store<BaseState>,
    private imageDrawService: ImageDrawService,
    private imageDataService: RawImageDataLoaderService,
    private firestoreService: FirebaseFirestoreService,
    private functions: FirebaseFunctionService
  ) {}
}

